import { DatePipe } from '@angular/common';
import { Injectable, TemplateRef } from '@angular/core';
import {
  addCellClassRules,
  AgGridCellPillComponent,
  AgGridCellTemplateRendererComponent,
  AgGridValueFormatters,
  EXCLUDE_COLUMN_FROM_EXPORT,
  LOCKED_COLUMN,
  NUMBER_COLUMN,
  PillColor,
} from '@anduin/shared';
import { CellClassParams, ColDef } from 'ag-grid-community';
import { addValueTooltip } from '../../utils/add-value-tooltip';
import { getPendingReasonCodeFilterOptions } from '../../utils/unsent-email-reason.utils';
import { PendingDisplayStatus, PendingEmailRow } from '../../models';

const PENDING_DISPLAY_STATUS_COLOR_MAP: Record<PendingDisplayStatus, PillColor> = {
  Delayed: 'yellow',
  Overdue: 'orange',
  'Retrying...': 'lavender',
  Dismissed: 'gray',
  Resolved: 'green',
};

@Injectable({ providedIn: 'root' })
export class EmailDeliveryHubPendingColumnBuilder {
  private datePipe = new DatePipe('en-US');

  buildPendingColumns(actionsTemplate: TemplateRef<unknown>, reasonFilterTemplateRef: TemplateRef<unknown>): ColDef<PendingEmailRow>[] {
    return [
      {
        headerName: 'Status',
        field: 'pendingDisplayStatus',
        width: 130,
        filter: null,
        sortable: false,
        cellRenderer: AgGridCellPillComponent,
        cellRendererParams: {
          pill: {
            getValue: ({ value }) => value,
            getColor: ({ value }) => PENDING_DISPLAY_STATUS_COLOR_MAP[value] ?? 'gray',
          },
        },
        cellClass: addCellClassRules({
          'fst-italic': (params: CellClassParams) => params.value === 'Retrying...',
        }),
      },
      {
        ...NUMBER_COLUMN(),
        headerName: 'Time in Queue',
        field: 'createdAt',
        sort: 'asc',
        flex: 1,
        filter: null,
        valueGetter: (p) => (p.data ? Date.now() - new Date(p.data.createdAt).getTime() : 0),
        valueFormatter: (p) => p.data.timeInQueue,
      },
      {
        headerName: 'Waiting On',
        field: 'reasonDisplay',
        colId: 'reasonCode',
        flex: 2,
        sortable: false,
        filter: 'customSetFilter',
        filterParams: {
          context: {
            reasonCode: {
              getSetFilterOptions: () => getPendingReasonCodeFilterOptions(reasonFilterTemplateRef),
            },
          },
        },
      },
      { headerName: 'Recommended Action', field: 'recommendedAction', flex: 2, filter: null, sortable: false },
      { headerName: 'Subject', field: 'subject', flex: 2 },
      {
        headerName: 'Recipients',
        field: 'allRecipients',
        flex: 2,
        valueFormatter: ({ value }) => value || 'Pending sync',
        ...addValueTooltip(true),
      },
      {
        headerName: 'Date Received',
        field: 'createdAt',
        flex: 1,
        valueFormatter: AgGridValueFormatters.DATE(this.datePipe, 'MM/dd/yyyy'),
      },
      {
        headerName: 'Last Updated',
        field: 'updatedAt',
        flex: 1,
        valueFormatter: AgGridValueFormatters.DATE(this.datePipe, 'MM/dd/yyyy'),
      },
      {
        ...LOCKED_COLUMN,
        headerName: '',
        type: EXCLUDE_COLUMN_FROM_EXPORT,
        width: 80,
        minWidth: 80,
        maxWidth: 80,
        pinned: 'right',
        cellRenderer: AgGridCellTemplateRendererComponent,
        cellRendererParams: {
          getTemplate: () => actionsTemplate,
        },
      },
    ];
  }
}
